import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, Printer, QrCode, Bluetooth, Check, Loader2, RotateCw, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { buildToken, renderQRSticker, downloadCanvasPNG } from "@/lib/qrLabel";
import {
  isBluetoothSupported, isPrinterConnected, connectPrinter,
  reconnectPrinter, printCanvas as btPrint,
} from "@/lib/btPrinter";

const QR_DIMS_DEFAULT = { widthMm: 80, heightMm: 60, dpi: 203, rotate: false };

/**
 * Printable QR sticker for a single form template.
 *
 * Prints directly to the Bluetooth thermal printer (TSPL, 203 dpi) using
 * the same `btPrinter` pipeline as the App Admin → Tenants QR sticker
 * flow — never the browser's system print dialog.
 *
 * Dimensions:
 *   • Prefilled from the platform's **QR sticker label size** (Brand
 *     Settings → QR sticker label size) so the very first print matches
 *     every other QR the operator handles.
 *   • Editable per-print inside this modal (Width / Height / Rotate 90°)
 *     as a belt-and-braces override for owners who need a one-off size
 *     or whose Brand Settings value hasn't propagated to production yet.
 *     Changes stay local to this modal and don't touch platform config.
 *
 * The QR payload is `VBE1:{t:"form", id:<template_id>}` — opaque to
 * generic camera apps, decoded only by the Vanguard Beacon app which
 * deep-links straight into the Fill modal for that template.
 */
export default function FormQRModal({ template, onClose }) {
  const canvasRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingSize, setSavingSize] = useState(false);
  const [connected, setConnected] = useState(isPrinterConnected());
  const [qty, setQty] = useState(1);

  // Editable per-print dimensions. `null` until cfg loads so we can seed
  // from the template's saved size (or Brand Settings) on first render
  // without briefly showing 80×60.
  const [widthMm, setWidthMm] = useState(null);
  const [heightMm, setHeightMm] = useState(null);
  const [rotate, setRotate] = useState(false);
  // Track the last-saved-per-form values so we can toggle the Save
  // button's enabled state ("dirty" indicator).
  const [savedDims, setSavedDims] = useState({
    widthMm: template.qr_label_width_mm ?? null,
    heightMm: template.qr_label_height_mm ?? null,
    rotate: !!template.qr_label_rotate,
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Load platform QR sticker dimensions and seed the editable fields.
  // Priority order (highest first):
  //   1. Per-form saved size on `template.qr_label_*` (Save size on
  //      this modal writes here). Persists across sessions.
  //   2. Tenant-wide Brand Settings QR sticker size, via the tenant-
  //      scoped `/food-prep/print-config` mirror.
  //   3. Hardcoded 80×60 default.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/food-prep/print-config");
        if (cancelled) return;
        setCfg(data);
        const tw = template.qr_label_width_mm;
        const th = template.qr_label_height_mm;
        const tr = template.qr_label_rotate;
        setWidthMm(tw || data.qr_label_width_mm || QR_DIMS_DEFAULT.widthMm);
        setHeightMm(th || data.qr_label_height_mm || QR_DIMS_DEFAULT.heightMm);
        setRotate(tr != null ? !!tr : !!data.qr_label_rotate);
      } catch {
        if (cancelled) return;
        setCfg({});
        setWidthMm(template.qr_label_width_mm || QR_DIMS_DEFAULT.widthMm);
        setHeightMm(template.qr_label_height_mm || QR_DIMS_DEFAULT.heightMm);
        setRotate(!!template.qr_label_rotate);
      }
    })();
    return () => { cancelled = true; };
  }, [template.id, template.qr_label_width_mm, template.qr_label_height_mm, template.qr_label_rotate]);

  const qrDims = useMemo(() => ({
    widthMm: Number(widthMm) || QR_DIMS_DEFAULT.widthMm,
    heightMm: Number(heightMm) || QR_DIMS_DEFAULT.heightMm,
    dpi: (cfg && cfg.label_dpi) || QR_DIMS_DEFAULT.dpi,
    rotate,
  }), [widthMm, heightMm, rotate, cfg]);

  const printerCfg = useMemo(() => ({
    ...(cfg || {}),
    label_width_mm: qrDims.widthMm,
    label_height_mm: qrDims.heightMm,
    label_dpi: qrDims.dpi,
    printer_command: (cfg && cfg.printer_command) || "tspl",
    // QR stickers use their own gap value (`qr_label_gap_mm`), falling
    // back to the food-prep gap and finally the 2 mm default.
    printer_gap_mm: (cfg && cfg.qr_label_gap_mm != null) ? cfg.qr_label_gap_mm
      : ((cfg && cfg.printer_gap_mm != null) ? cfg.printer_gap_mm : 2),
  }), [cfg, qrDims]);

  useEffect(() => {
    if (widthMm == null || heightMm == null) return;  // wait for dims
    let cancelled = false;
    (async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const token = buildToken("form", template.id);
      const lines = [
        (template.name || "Form").toUpperCase(),
        template.category || "Vanguard Beacon",
        "Scan with the Vanguard Beacon app",
      ];
      await renderQRSticker(canvas, token, lines, qrDims);
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [template.id, template.name, template.category, qrDims, widthMm, heightMm]);

  const fileBase = `QR_Form_${(template.name || "form").replace(/[^\w\-]+/g, "_")}`;
  const download = () => downloadCanvasPNG(canvasRef.current, fileBase);

  const ensureConnected = async () => {
    if (!isBluetoothSupported()) {
      throw new Error("Direct printing needs Chrome on an Android device.");
    }
    if (!isPrinterConnected()) {
      const ok = await reconnectPrinter(printerCfg);
      if (!ok) await connectPrinter(printerCfg);
    }
    setConnected(true);
  };

  const connect = async () => {
    setBusy(true);
    try {
      await ensureConnected();
      toast.success("Printer connected");
    } catch (e) {
      toast.error(e.message || "Couldn't connect");
    } finally { setBusy(false); }
  };

  const print = async () => {
    setBusy(true);
    try {
      await ensureConnected();
      const q = Math.max(1, parseInt(qty, 10) || 1);
      await btPrint(canvasRef.current, printerCfg, q);
      toast.success(`Sent ${q} sticker${q === 1 ? "" : "s"} to the printer`);
    } catch (e) {
      toast.error(e.message || "Print failed");
    } finally { setBusy(false); }
  };

  const dimsLoaded = widthMm != null && heightMm != null;

  const isDirty =
    dimsLoaded && (
      Number(widthMm) !== Number(savedDims.widthMm ?? cfg?.qr_label_width_mm ?? QR_DIMS_DEFAULT.widthMm) ||
      Number(heightMm) !== Number(savedDims.heightMm ?? cfg?.qr_label_height_mm ?? QR_DIMS_DEFAULT.heightMm) ||
      Boolean(rotate) !== Boolean(savedDims.rotate ?? cfg?.qr_label_rotate ?? false)
    );

  const saveSize = async () => {
    if (!dimsLoaded) return;
    setSavingSize(true);
    try {
      await api.put(`/forms/templates/${template.id}/qr-label`, {
        width_mm: Number(widthMm),
        height_mm: Number(heightMm),
        rotate: !!rotate,
      });
      setSavedDims({ widthMm: Number(widthMm), heightMm: Number(heightMm), rotate: !!rotate });
      toast.success("Sticker size saved for this form");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save size");
    } finally {
      setSavingSize(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="form-qr-modal"
    >
      <div
        className="vb-card p-0 w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold inline-flex items-center gap-2">
              <QrCode size={18} /> Form QR
            </h2>
            <p className="text-xs opacity-70 mt-0.5">{template.name}</p>
          </div>
          <button className="vb-btn ghost" onClick={onClose} data-testid="form-qr-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div
            className="vb-card p-3 flex items-center justify-between gap-2 flex-wrap"
            style={{ background: "var(--vb-bg)" }}
          >
            <span className="text-sm flex items-center gap-2">
              {connected
                ? <><Check size={15} style={{ color: "#16a34a" }} /> Printer connected</>
                : <><Bluetooth size={15} /> Printer not connected</>}
            </span>
            {!connected && (
              <button
                className="vb-btn sm"
                disabled={busy}
                onClick={connect}
                data-testid="form-qr-connect"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Bluetooth size={14} />}
                Connect printer
              </button>
            )}
          </div>

          <div
            className="flex justify-center"
            style={{ background: "#f5f1ea", padding: 12, borderRadius: 10 }}
          >
            <canvas
              ref={canvasRef}
              style={{ maxWidth: "100%", width: "100%", height: "auto", background: "#fff" }}
              data-testid="form-qr-canvas"
            />
          </div>

          {/* Per-print dimension override.
              Seeded from the form's saved size, or Brand Settings if
              none saved yet. Tap Save size to persist per-form so
              future prints on this template default to the same size. */}
          <div className="vb-card p-3" style={{ background: "var(--vb-bg)" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold opacity-80">Sticker size</div>
              <button
                className="vb-btn sm ghost"
                onClick={saveSize}
                disabled={!dimsLoaded || !isDirty || savingSize}
                title={isDirty ? "Save this size as the default for this form"
                               : "Size already saved — edit width/height/rotate to enable"}
                data-testid="form-qr-save-size"
                style={{ padding: "4px 10px", fontSize: 12 }}
              >
                {savingSize ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save size
              </button>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <label className="text-xs">
                <span className="block opacity-70 mb-1">Width (mm)</span>
                <input
                  type="number" min="15" max="150"
                  value={widthMm ?? ""}
                  onChange={(e) => setWidthMm(e.target.value === "" ? "" : Number(e.target.value))}
                  className="vb-input"
                  style={{ width: 84, padding: "4px 8px" }}
                  data-testid="form-qr-width"
                />
              </label>
              <label className="text-xs">
                <span className="block opacity-70 mb-1">Height (mm)</span>
                <input
                  type="number" min="15" max="150"
                  value={heightMm ?? ""}
                  onChange={(e) => setHeightMm(e.target.value === "" ? "" : Number(e.target.value))}
                  className="vb-input"
                  style={{ width: 84, padding: "4px 8px" }}
                  data-testid="form-qr-height"
                />
              </label>
              <label className="text-xs flex items-center gap-1.5 cursor-pointer select-none mb-1">
                <input
                  type="checkbox"
                  checked={rotate}
                  onChange={(e) => setRotate(e.target.checked)}
                  data-testid="form-qr-rotate"
                  style={{ width: 15, height: 15 }}
                />
                <RotateCw size={12} /> Rotate 90°
              </label>
            </div>
            <p className="text-[11px] opacity-60 mt-2">
              Tap <strong>Save size</strong> to remember this size for this form. Otherwise the modal
              falls back to Brand Settings → QR sticker label size.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs opacity-70">Qty</label>
            <input
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="vb-input"
              style={{ width: 70, padding: "4px 8px" }}
              data-testid="form-qr-qty"
            />
            <p className="text-xs opacity-60 ml-auto" data-testid="form-qr-dims-label">
              Prints at <strong>{qrDims.widthMm} × {qrDims.heightMm} mm</strong>
              {rotate ? " · rotated 90°" : ""} · TSPL 203 dpi
            </p>
          </div>

          <p className="text-xs opacity-70">
            Load your QR sticker roll, connect the Bluetooth thermal printer, then tap Print.
            The QR opens the fill screen directly when scanned inside the Vanguard Beacon app.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <button
            className="vb-btn ghost"
            onClick={download}
            disabled={!ready}
            data-testid="form-qr-download"
          >
            <Download size={14} /> Download PNG
          </button>
          <button
            className="vb-btn"
            onClick={print}
            disabled={!ready || busy || !dimsLoaded}
            data-testid="form-qr-print"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
            Print
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
